import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ArrowDown, X, Hexagon, Loader2 } from "lucide-react";
import { TOUR_STEPS, TOUR_PHASES, TOUR_PIPELINE, TOUR_CHAINS } from "@/lib/tour";

const POPOVER_W = 340;

export const TourOverlay = ({ stepIdx, onNext, onBack, onSkip, onFinish }) => {
  const rawStep = TOUR_STEPS[stepIdx];
  const intelScenario = typeof document !== "undefined"
    ? document.querySelector('[data-testid="intelligence-panel"]')?.getAttribute("data-intel-scenario")
    : null;
  const vkey = ["withdrawal", "withdrawal-pt"].includes(intelScenario) ? "withdrawal"
    : ["retention", "hv-esc", "healthy"].includes(intelScenario) ? "client"
    : ["kyc", "compliance"].includes(intelScenario) ? "kyc"
    : intelScenario === "payments-esc" ? "payment" : "default";
  const step = { ...rawStep, ...(rawStep.variants?.[vkey] || {}) };
  const phaseLabel = rawStep.phaseLabel || TOUR_PHASES[rawStep.phase - 1];
  const [rect, setRect] = useState(null);
  const popRef = useRef(null);
  const [popH, setPopH] = useState(0);

  useEffect(() => {
    setRect(null);
    const update = () => {
      if (popRef.current) {
        const h = popRef.current.offsetHeight;
        setPopH((p) => (p === h ? p : h));
      }
      const el = step.target ? document.querySelector(`[data-testid="${step.target}"]`) : null;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.bottom < 72 || r.top > window.innerHeight - 80) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      setRect((p) =>
        p && Math.abs(p.top - r.top) < 2 && Math.abs(p.left - r.left) < 2 && p.width === r.width && p.height === r.height
          ? p
          : { top: r.top, left: r.left, width: r.width, height: r.height }
      );
    };
    const iv = setInterval(update, 350);
    update();
    window.addEventListener("resize", update);
    return () => { clearInterval(iv); window.removeEventListener("resize", update); };
  }, [stepIdx, step.target]);

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(POPOVER_W, vw - 16);
  const waiting = Boolean(step.target) && !rect;
  const attached = Boolean(rect) && !step.center;

  let popStyle;
  if (!attached) {
    popStyle = { left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: w, maxHeight: vh - 24 };
  } else {
    const h = Math.min(popH || 260, vh - 16);
    const left = Math.min(Math.max(8, rect.left), vw - w - 8);
    const belowTop = rect.top + rect.height + 12;
    const spaceBelow = vh - belowTop - 8;
    const spaceAbove = rect.top - 12 - 8;
    let top;
    if (h >= vh - 16) {
      top = 8;
    } else if (spaceBelow >= h || spaceBelow >= spaceAbove) {
      top = Math.min(belowTop, vh - h - 8);
    } else {
      top = rect.top - 12 - h;
    }
    popStyle = { left, top: Math.max(8, Math.min(top, vh - h - 8)), width: w, maxHeight: vh - 16 };
  }

  return (
    <>
      {attached && (
        <div
          data-testid="tour-highlight"
          className="fixed z-40 pointer-events-none border-2 border-emerald-400 rounded-md shadow-[0_0_0_4px_rgba(16,185,129,0.15),0_0_24px_rgba(16,185,129,0.35)] transition-all duration-300"
          style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 8, height: rect.height + 8 }}
        />
      )}
      <div
        ref={popRef}
        data-testid="tour-popover"
        className="fixed z-50 bg-zinc-900 border border-emerald-500/40 rounded-md shadow-[0_8px_40px_rgba(0,0,0,0.6),0_0_20px_rgba(16,185,129,0.12)] p-5 overflow-y-auto"
        style={popStyle}
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-[9px] font-mono tracking-widest text-emerald-500 uppercase" data-testid="tour-step-indicator">
            Step {step.phase} of 8 — {phaseLabel}
          </span>
          <button
            data-testid="tour-skip-button"
            onClick={() => onSkip()}
            className="flex items-center gap-1 text-[10px] font-mono font-semibold tracking-wider text-zinc-500 hover:text-zinc-200 transition-colors duration-150 px-1.5 py-1 whitespace-nowrap shrink-0"
          >
            SKIP TOUR <X className="w-3 h-3" strokeWidth={2} />
          </button>
        </div>

        <div className="flex items-start gap-2.5 mb-2">
          <Hexagon className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" strokeWidth={1.5} />
          <div className="text-sm font-bold text-zinc-100 font-display tracking-wide leading-snug" data-testid="tour-title">
            {step.title}
          </div>
        </div>

        {step.body && <p className="text-xs text-zinc-400 leading-relaxed mb-3">{step.body}</p>}

        {step.list && (
          <div className="space-y-1.5 mb-3">
            {step.list.map((item) => (
              <div key={item} className="flex items-start gap-2 text-[11px] text-zinc-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                {item}
              </div>
            ))}
          </div>
        )}

        {step.pipeline && (
          <div className="mb-3 space-y-0" data-testid="tour-pipeline">
            {TOUR_PIPELINE.map((node, i) => (
              <div key={node}>
                <div className={`text-[10px] font-mono tracking-wider px-2.5 py-1.5 rounded-sm border ${
                  i === 0 || i === TOUR_PIPELINE.length - 1
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                    : "border-zinc-800 bg-zinc-950/60 text-zinc-300"
                }`}>
                  {node}
                </div>
                {i < TOUR_PIPELINE.length - 1 && (
                  <div className="pl-3 py-0"><ArrowDown className="w-3 h-3 text-emerald-500/60" strokeWidth={1.5} /></div>
                )}
              </div>
            ))}
          </div>
        )}

        {step.compare && (
          <div className="mb-3 space-y-2" data-testid="tour-compare">
            <div className="border border-zinc-800 bg-zinc-950/60 rounded-sm px-2.5 py-2">
              <div className="text-[9px] font-mono tracking-widest text-zinc-500 mb-1">TRADITIONAL CRM</div>
              <div className="text-[10px] font-mono text-zinc-400 leading-relaxed">Lead received → static score → workflow</div>
            </div>
            <div className="border border-emerald-500/40 bg-emerald-500/10 rounded-sm px-2.5 py-2">
              <div className="text-[9px] font-mono tracking-widest text-emerald-500 mb-1">O.N.E.TECH</div>
              <div className="text-[10px] font-mono text-emerald-300/90 leading-relaxed">Signal received → interpret → re-evaluate → classify → explain → determine Next Best Action → act / escalate → write intelligence back to CRM</div>
            </div>
          </div>
        )}

        {step.chain && (() => {
          const last = localStorage.getItem("onet_tour_last_case") || "ftd";
          const chain = TOUR_CHAINS[last] || TOUR_CHAINS.ftd;
          return (
            <div className="mb-3" data-testid="tour-chain">
              {chain.map(([label, text], i) => (
                <div key={label}>
                  <div className={`rounded-sm border px-2.5 py-1.5 ${i === chain.length - 1 ? "border-emerald-500/40 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950/60"}`}>
                    <div className={`text-[8px] font-mono tracking-widest ${i === chain.length - 1 ? "text-emerald-500" : "text-zinc-500"}`}>{label}</div>
                    <div className={`text-[10px] font-mono leading-snug ${i === chain.length - 1 ? "text-emerald-300" : "text-zinc-300"}`}>{text}</div>
                  </div>
                  {i < chain.length - 1 && <div className="pl-3 py-0"><ArrowDown className="w-3 h-3 text-emerald-500/60" strokeWidth={1.5} /></div>}
                </div>
              ))}
            </div>
          );
        })()}

        {step.finalNote && (
          <div className="mb-2 border-l-2 border-emerald-500/60 bg-emerald-500/5 rounded-sm px-3 py-2 text-[11px] text-zinc-300 leading-relaxed" data-testid="tour-final-note">
            {step.finalNote}
          </div>
        )}

        {waiting && (
          <div className="flex items-center gap-2 text-[10px] font-mono text-emerald-500/80 mb-2" data-testid="tour-waiting">
            <Loader2 className="w-3 h-3 animate-spin" /> Preparing the simulation step…
          </div>
        )}

        {step.final ? (
          <div className="space-y-2 mt-4">
            <button
              data-testid="tour-explore-live-button"
              onClick={() => onFinish("/live")}
              className="w-full flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-sm text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-zinc-950 transition-colors duration-200"
            >
              EXPLORE LIVE OPERATIONS <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
            <button
              data-testid="tour-view-impact-button"
              onClick={() => onFinish("/impact")}
              className="w-full flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-sm text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 transition-colors duration-200"
            >
              VIEW EXECUTIVE IMPACT
            </button>
            <button
              data-testid="tour-return-dashboard-button"
              onClick={() => onFinish("/")}
              className="w-full flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-sm text-xs font-semibold text-zinc-400 hover:text-zinc-100 border border-zinc-800 hover:border-zinc-600 transition-colors duration-200"
            >
              RETURN TO DASHBOARD
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 mt-4">
            <button
              data-testid="tour-back-button"
              onClick={onBack}
              disabled={stepIdx === 0}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-sm text-[11px] font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> BACK
            </button>
            <button
              data-testid="tour-next-button"
              onClick={onNext}
              className="flex-1 flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-sm text-[11px] font-semibold bg-emerald-500 hover:bg-emerald-600 text-zinc-950 transition-colors duration-200"
            >
              {step.next || "NEXT"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          </div>
        )}
      </div>
    </>
  );
};
